import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { parseISO } from "date-fns";
import { useAuth } from "./hooks/useAuth";
import { useAppConfig } from "./hooks/useAppConfig";
import { currentDay } from "./lib/days";
import { LoadingScreen } from "./components/LoadingScreen";
import { Layout } from "./components/Layout";
import { SignInPage } from "./pages/SignInPage";
import { AccessDeniedPage } from "./pages/AccessDeniedPage";
import { TestingNotStartedPage } from "./pages/TestingNotStartedPage";
import { TestingCompletePage } from "./pages/TestingCompletePage";
import { DashboardPage } from "./pages/DashboardPage";

// Admin code splits into a separate chunk. ~130 testers will never load
// it; only Dan, Amanda, and the Retronyms email do.
const AdminLayout = lazy(() =>
  import("./components/admin/AdminLayout").then((m) => ({ default: m.AdminLayout })),
);
const OverviewPage = lazy(() =>
  import("./pages/admin/OverviewPage").then((m) => ({ default: m.OverviewPage })),
);
const EngagementPage = lazy(() =>
  import("./pages/admin/EngagementPage").then((m) => ({ default: m.EngagementPage })),
);
const FailedConditionsPage = lazy(() =>
  import("./pages/admin/FailedConditionsPage").then((m) => ({ default: m.FailedConditionsPage })),
);
const CoveragePage = lazy(() =>
  import("./pages/admin/CoveragePage").then((m) => ({ default: m.CoveragePage })),
);
const RosterPage = lazy(() =>
  import("./pages/admin/RosterPage").then((m) => ({ default: m.RosterPage })),
);
const ConfigPage = lazy(() =>
  import("./pages/admin/ConfigPage").then((m) => ({ default: m.ConfigPage })),
);
const ChecksPage = lazy(() =>
  import("./pages/admin/ChecksPage").then((m) => ({ default: m.ChecksPage })),
);

/**
 * Top-level routing.
 *   /                 → tester dashboard (or sign-in / state pages)
 *   /admin/*          → admin panel (gated on isAdmin)
 *
 * Auth state is checked first, then route matched. Non-admins hitting /admin
 * are redirected back to /.
 */
export default function App() {
  const auth = useAuth();
  const cfg = useAppConfig(!!auth.session);
  const location = useLocation();

  if (auth.sessionLoading) {
    return <LoadingScreen message="Checking session..." />;
  }

  if (!auth.session) {
    return <SignInPage onSignIn={auth.signInWithGoogle} />;
  }

  if (auth.profileLoading || cfg.loading) {
    return <LoadingScreen message="Loading your profile..." />;
  }

  const config = cfg.config;
  const profile = auth.profile;
  const signedInEmail = auth.session.user.email ?? null;
  const contactEmail = config?.contact_email || "app.testing@teamrwb.org";
  const isAdminPath = location.pathname.startsWith("/admin");

  // Non-admin trying to reach /admin → bounce to /
  if (isAdminPath && !auth.isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Signed in, no tester row, not admin → access denied
  if (!profile && !auth.isAdmin) {
    return (
      <AccessDeniedPage
        signedInEmail={signedInEmail}
        contactEmail={contactEmail}
        onSignOut={auth.signOut}
      />
    );
  }

  // /admin/* — render the admin shell. Lazy-loaded as a separate chunk.
  if (isAdminPath) {
    return (
      <Suspense fallback={<LoadingScreen message="Loading admin panel..." />}>
        <AdminLayout signedInEmail={signedInEmail} onSignOut={auth.signOut}>
          <Routes>
            <Route path="/admin" element={<Navigate to="/admin/overview" replace />} />
            <Route path="/admin/overview" element={<OverviewPage />} />
            <Route path="/admin/engagement" element={<EngagementPage />} />
            <Route path="/admin/failed" element={<FailedConditionsPage />} />
            <Route path="/admin/coverage" element={<CoveragePage />} />
            <Route path="/admin/roster" element={<RosterPage />} />
            <Route path="/admin/config" element={<ConfigPage />} />
            <Route path="/admin/checks" element={<ChecksPage />} />
            <Route path="/admin/*" element={<Navigate to="/admin/overview" replace />} />
          </Routes>
        </AdminLayout>
      </Suspense>
    );
  }

  // Tester dashboard path → /
  // Admin without tester row falls through here too; show admin welcome.
  if (!profile && auth.isAdmin) {
    return (
      <Layout profile={null} isAdmin onSignOut={auth.signOut}>
        <div className="px-4 py-6 max-w-md mx-auto text-center">
          <h2 className="text-lg font-semibold text-zinc-900 mb-1">Welcome, admin</h2>
          <p className="text-sm text-zinc-500 mb-3">
            Signed in as <span className="font-mono text-xs">{signedInEmail}</span>
          </p>
          <p className="text-xs text-zinc-500 leading-relaxed mb-4">
            You're not on the testers roster, so the tester dashboard isn't shown.
          </p>
          <a
            href={`${import.meta.env.BASE_URL}admin`}
            className="inline-block bg-rwb-red text-white text-sm font-medium px-5 py-2 rounded-lg hover:bg-rwb-redDark transition"
          >
            Open admin panel
          </a>
        </div>
      </Layout>
    );
  }

  if (!profile || !config) {
    return <LoadingScreen message="Loading..." />;
  }

  const today = new Date();
  const dayInfo = currentDay(profile.tester_type, today, {
    staffStartDate: config.staff_start_date,
    staffWindowDays: config.staff_window_days,
    volunteerStartDate: config.volunteer_start_date,
    volunteerWindowDays: config.volunteer_window_days,
  });

  if (dayInfo.isBeforeStart) {
    const startISO =
      profile.tester_type === "Volunteer" ? config.volunteer_start_date : config.staff_start_date;
    return (
      <Layout profile={profile} isAdmin={auth.isAdmin} onSignOut={auth.signOut}>
        <TestingNotStartedPage
          profile={profile}
          startDateISO={startISO}
          contactEmail={contactEmail}
        />
      </Layout>
    );
  }

  if (dayInfo.isComplete) {
    return (
      <Layout profile={profile} isAdmin={auth.isAdmin} onSignOut={auth.signOut}>
        <TestingCompletePage profile={profile} contactEmail={contactEmail} />
      </Layout>
    );
  }

  const cohortStartISO =
    profile.tester_type === "Volunteer" ? config.volunteer_start_date : config.staff_start_date;
  const cohortStart = parseISO(cohortStartISO);

  return (
    <Layout profile={profile} isAdmin={auth.isAdmin} onSignOut={auth.signOut}>
      <DashboardPage
        profile={profile}
        dayInfo={dayInfo}
        cohortStart={cohortStart}
        today={today}
        broadcastNote={config.global_broadcast_note}
        asanaUrl={config.asana_bug_form_url}
        conditionConfig={cfg.conditionConfig}
      />
    </Layout>
  );
}
